import moment from "moment-timezone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BsClock } from "react-icons/bs";
import { useSelector } from "react-redux";

import {
    color_grey_dark,
    color_grey_light,
    color_red_default,
    color_white,
} from "../../../constants/colors";
import {
    bottomSheetDragHandleVisualStyle,
    useBottomSheetDrag,
} from "../../../hooks/useBottomSheetDrag";
import { useWindowDimensions } from "../../../utils";
import { ModalDefault } from "../../containers/ModalDefault";
import { Button } from "../../controls/Button/Button";
import IconlyCurvedLightCalendar from "../../icons/IconlyCurvedLightCalendar";
import { OnestBoldBig, OnestNormalSmall } from "../../styled/TextComponents";
import { useI18n } from "../../../i18n/I18nProvider";

const MAX_PERSONAL_EVENT_TITLE_LENGTH = 12;
const MAX_PERSONAL_EVENT_NOTE_LENGTH = 24;

const ModalPersonalTask = ({
  isOpen,
  onClose,
  onSave,
  initialTask,
  existingEvents = [],
}) => {
  const { t } = useI18n();
  const { width: screenWidth } = useWindowDimensions();
  const isMobile = screenWidth <= 540;
  const user = useSelector((state) => state.user);
  const timeZone =
    user.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const resolveBaseDate = useCallback(
    (task) => {
      if (task?.dateDate) {
        return moment(task.dateDate);
      }
      return moment().tz(timeZone);
    },
    [timeZone],
  );

  const baseDate = resolveBaseDate(initialTask);
  const defaultStart = initialTask?.dateDate
    ? moment(initialTask.dateDate).format("HH:mm")
    : "";
  const defaultEnd = initialTask?.endDateDate
    ? moment(initialTask.endDateDate).format("HH:mm")
    : "";

  const [title, setTitle] = useState(initialTask?.title || "");
  const [date, setDate] = useState(baseDate.format("YYYY-MM-DD"));
  const [startTime, setStartTime] = useState(defaultStart || "");
  const [endTime, setEndTime] = useState(defaultEnd || "");
  const [note, setNote] = useState(initialTask?.note || "");
  const [isSaving, setIsSaving] = useState(false);
  const [openedPicker, setOpenedPicker] = useState(null);
  const dateInputRef = useRef(null);
  const startTimeInputRef = useRef(null);
  const endTimeInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const nextBaseDate = resolveBaseDate(initialTask);
    const nextStart = initialTask?.dateDate
      ? moment(initialTask.dateDate).format("HH:mm")
      : "";
    const nextEnd = initialTask?.endDateDate
      ? moment(initialTask.endDateDate).format("HH:mm")
      : "";
    setTitle(initialTask?.title || "");
    setDate(nextBaseDate.format("YYYY-MM-DD"));
    setStartTime(nextStart || "");
    setEndTime(nextEnd || "");
    setNote(initialTask?.note || "");
    setIsSaving(false);
    setOpenedPicker(null);
  }, [initialTask, isOpen, resolveBaseDate]);

  const toggleNativePicker = useCallback(
    (pickerKey, inputRef) => {
      const input = inputRef.current;
      if (!input) {
        return;
      }

      if (openedPicker === pickerKey) {
        input.blur();
        setOpenedPicker(null);
        return;
      }

      input.focus();
      try {
        if (typeof input.showPicker === "function") {
          input.showPicker();
        } else {
          input.click?.();
        }
      } catch {
        input.click?.();
      }
      setOpenedPicker(pickerKey);
    },
    [openedPicker],
  );

  const handleSubmit = async () => {
    if (isSaving) {
      return;
    }
    if (!title.trim()) {
      return;
    }
    if (
      title.length > MAX_PERSONAL_EVENT_TITLE_LENGTH ||
      note.length > MAX_PERSONAL_EVENT_NOTE_LENGTH
    ) {
      return;
    }
    const start = moment(`${date} ${startTime}`, "YYYY-MM-DD HH:mm");
    const end = moment(`${date} ${endTime}`, "YYYY-MM-DD HH:mm");
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
      return;
    }
    if (conflictEvent) {
      return;
    }
    const payload = {
      title: title.trim(),
      start_time: start.utc().format("YYYY-MM-DDTHH:mm:ss"),
      end_time: end.utc().format("YYYY-MM-DDTHH:mm:ss"),
      note: note?.trim() ? note.trim() : null,
    };
    setIsSaving(true);
    try {
      const ok = await onSave(payload, initialTask?.personalEventId || null);
      if (ok) {
        onClose();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const conflictEvent = useMemo(() => {
    const start = moment(`${date} ${startTime}`, "YYYY-MM-DD HH:mm");
    const end = moment(`${date} ${endTime}`, "YYYY-MM-DD HH:mm");
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
      return null;
    }

    return (
      existingEvents.find((event) => {
        if (!event?.dateDate) {
          return false;
        }
        if (
          initialTask?.personalEventId &&
          event.eventType === "personal" &&
          event.personalEventId === initialTask.personalEventId
        ) {
          return false;
        }
        const eventStart = moment(event.dateDate);
        const eventEnd = moment(event.endDateDate || event.dateDate);
        return eventStart.isBefore(end) && eventEnd.isAfter(start);
      }) || null
    );
  }, [date, startTime, endTime, existingEvents, initialTask?.personalEventId]);

  const conflictText = useMemo(() => {
    if (!conflictEvent) {
      return "";
    }
    const eventStart = moment(conflictEvent.dateDate).format("HH:mm");
    const eventEnd = moment(
      conflictEvent.endDateDate || conflictEvent.dateDate,
    ).format("HH:mm");
    const subject =
      conflictEvent.eventType === "personal"
        ? conflictEvent.title
        : conflictEvent.studentName;
    return t("Это время занято: {{subject}} ({{start}}–{{end}})", {
      subject,
      start: eventStart,
      end: eventEnd,
    });
  }, [conflictEvent, t]);

  const isTitleValid = title.trim().length > 0;
  const isTitleLengthValid = title.length <= MAX_PERSONAL_EVENT_TITLE_LENGTH;
  const isNoteLengthValid = note.length <= MAX_PERSONAL_EVENT_NOTE_LENGTH;
  const isRangeValid = useMemo(() => {
    const start = moment(`${date} ${startTime}`, "YYYY-MM-DD HH:mm");
    const end = moment(`${date} ${endTime}`, "YYYY-MM-DD HH:mm");
    return start.isValid() && end.isValid() && end.isAfter(start);
  }, [date, startTime, endTime]);
  const canSave =
    isTitleValid &&
    isTitleLengthValid &&
    isNoteLengthValid &&
    isRangeValid &&
    !conflictEvent &&
    !isSaving;

  const { dragHandleProps, sheetMotionStyle, resetDrag } = useBottomSheetDrag({
    enabled: isMobile,
    onClose,
  });

  useEffect(() => {
    if (!isOpen) {
      resetDrag();
    }
  }, [isOpen, resetDrag]);

  return (
    <ModalDefault
      isOpen={isOpen}
      onRequestClose={onClose}
      centerAlways={!isMobile}
      mobileBottomSheet={isMobile}
    >
      <div
        style={{
          width: isMobile ? "100%" : 418,
          maxWidth: isMobile ? "100vw" : "calc(100vw - 24px)",
          backgroundColor: color_white,
          borderRadius: isMobile ? "20px 20px 0 0" : 20,
          padding: isMobile
            ? "14px 16px calc(28px + env(safe-area-inset-bottom))"
            : "24px 24px 20px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? 14 : 10,
          marginTop: isMobile ? "auto" : 0,
          ...(isMobile ? sheetMotionStyle : {}),
        }}
      >
        <style>
          {`
            .personal-task-native-input {
              min-height: 48px;
              height: 50px;
              font-size: 16px;
              line-height: 1.2;
              box-sizing: border-box;
              -webkit-appearance: none;
              appearance: none;
            }

            .personal-task-native-input[type="date"],
            .personal-task-native-input[type="time"] {
              padding-right: 36px;
            }

            .personal-task-native-input::-webkit-date-and-time-value {
              text-align: left;
            }

            .personal-task-native-input::-webkit-datetime-edit,
            .personal-task-native-input::-webkit-date-and-time-value,
            .personal-task-native-input::-webkit-datetime-edit-fields-wrapper {
              padding: 0;
              margin: 0;
            }

            .personal-task-native-input::-webkit-calendar-picker-indicator {
              opacity: 0;
              cursor: pointer;
              margin: 0;
              padding: 0;
              width: 18px;
              height: 18px;
            }
          `}
        </style>
        {isMobile && (
          <div {...dragHandleProps}>
            <div style={bottomSheetDragHandleVisualStyle} />
          </div>
        )}
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          <OnestBoldBig
            style={{ fontSize: isMobile ? 22 : 24, lineHeight: 1.1 }}
          >
            {t(initialTask ? "Редактировать событие" : "Новое событие")}
          </OnestBoldBig>
          <div style={{ flexGrow: 1 }} />
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            style={{
              border: "none",
              background: "transparent",
              cursor: isSaving ? "default" : "pointer",
              padding: 12,
              minWidth: 44,
              minHeight: 44,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginTop: isMobile ? -6 : -8,
              marginRight: isMobile ? -10 : -6,
              opacity: isSaving ? 0.6 : 1,
            }}
            aria-label={t("Закрыть")}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18"
                stroke="#B1B1B1"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6 6L18 18"
                stroke="#B1B1B1"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t("Название")}
          maxLength={MAX_PERSONAL_EVENT_TITLE_LENGTH}
          style={{
            height: 50,
            borderRadius: 10,
            border: "none",
            backgroundColor: "#F6F6F6",
            padding: "0 14px",
            fontFamily: "Onest",
            fontSize: 16,
          }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? 10 : 8,
          }}
        >
          <div style={{ position: "relative", width: "100%" }}>
            <input
              ref={dateInputRef}
              className="personal-task-native-input"
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setOpenedPicker(null);
              }}
              onBlur={() => {
                if (openedPicker === "date") {
                  setOpenedPicker(null);
                }
              }}
              style={{
                width: "100%",
                borderRadius: 10,
                border: "none",
                backgroundColor: "#F6F6F6",
                padding: "0 14px",
                fontFamily: "Onest",
                fontSize: 16,
                color: date ? "#222222" : color_grey_dark,
              }}
            />
            <button
              type="button"
              aria-label={t("Открыть или закрыть выбор даты")}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleNativePicker("date", dateInputRef);
              }}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                border: "none",
                background: "transparent",
                padding: 4,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconlyCurvedLightCalendar
                width={20}
                height={20}
                stroke="#B1B1B1"
              />
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: isMobile ? 10 : 8,
            }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <OnestNormalSmall style={{ color: color_grey_dark }}>
                {t("Начало")}
              </OnestNormalSmall>
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  ref={startTimeInputRef}
                  className="personal-task-native-input"
                  type="time"
                  value={startTime}
                  onChange={(e) => {
                    setStartTime(e.target.value);
                    setOpenedPicker(null);
                  }}
                  onBlur={() => {
                    if (openedPicker === "start") {
                      setOpenedPicker(null);
                    }
                  }}
                  step={600}
                  style={{
                    width: "100%",
                    borderRadius: 10,
                    border: "none",
                    backgroundColor: "#F6F6F6",
                    padding: "0 14px",
                    fontFamily: "Onest",
                    fontSize: 16,
                    color: startTime ? "#222222" : color_grey_dark,
                  }}
                />
                <button
                  type="button"
                  aria-label={t("Открыть или закрыть время начала")}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleNativePicker("start", startTimeInputRef);
                  }}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "transparent",
                    padding: 4,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BsClock size={16} color="#B1B1B1" />
                </button>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <OnestNormalSmall style={{ color: color_grey_dark }}>
                {t("Окончание (тот же день)")}
              </OnestNormalSmall>
              <div style={{ position: "relative", width: "100%" }}>
                <input
                  ref={endTimeInputRef}
                  className="personal-task-native-input"
                  type="time"
                  value={endTime}
                  onChange={(e) => {
                    setEndTime(e.target.value);
                    setOpenedPicker(null);
                  }}
                  onBlur={() => {
                    if (openedPicker === "end") {
                      setOpenedPicker(null);
                    }
                  }}
                  step={600}
                  style={{
                    width: "100%",
                    borderRadius: 10,
                    border: "none",
                    backgroundColor: "#F6F6F6",
                    padding: "0 14px",
                    fontFamily: "Onest",
                    fontSize: 16,
                    color: endTime ? "#222222" : color_grey_dark,
                  }}
                />
                <button
                  type="button"
                  aria-label={t("Открыть или закрыть время окончания")}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleNativePicker("end", endTimeInputRef);
                  }}
                  style={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "transparent",
                    padding: 4,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BsClock size={16} color="#B1B1B1" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("Комментарий (необязательно)")}
          maxLength={MAX_PERSONAL_EVENT_NOTE_LENGTH}
          rows={3}
          style={{
            borderRadius: 10,
            border: "none",
            backgroundColor: "#F6F6F6",
            padding: 12,
            resize: "vertical",
            fontFamily: "Onest",
            fontSize: 16,
          }}
        />
        {conflictText && (
          <OnestNormalSmall style={{ color: color_red_default, marginTop: -2 }}>
            {conflictText}
          </OnestNormalSmall>
        )}
        {!isTitleLengthValid && (
          <OnestNormalSmall style={{ color: color_red_default, marginTop: -2 }}>
            {t("Название: максимум {{count}} символов", { count: MAX_PERSONAL_EVENT_TITLE_LENGTH })}
          </OnestNormalSmall>
        )}
        {!isNoteLengthValid && (
          <OnestNormalSmall style={{ color: color_red_default, marginTop: -2 }}>
            {t("Комментарий: максимум {{count}} символа", { count: MAX_PERSONAL_EVENT_NOTE_LENGTH })}
          </OnestNormalSmall>
        )}
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            flexDirection: isMobile ? "column" : "row",
            marginTop: 2,
          }}
        >
          <Button
            type="button"
            width={isMobile ? "100%" : 154}
            height={44}
            disabled={!canSave}
            backgroundColor={canSave ? color_red_default : color_grey_light}
            color={color_white}
            borderColor={canSave ? color_red_default : color_grey_light}
            onClick={handleSubmit}
            fontSize={14}
            style={{ borderRadius: 100 }}
          >
            {t(isSaving ? "Сохранение..." : "Сохранить")}
          </Button>
          <Button
            type="button"
            width={isMobile ? "100%" : 154}
            height={44}
            disabled={isSaving}
            backgroundColor={color_white}
            color={color_grey_dark}
            borderColor="#D8D8D8"
            onClick={onClose}
            fontSize={14}
            style={{ borderRadius: 100 }}
          >
            {t("Отмена")}
          </Button>
        </div>
      </div>
    </ModalDefault>
  );
};

export default ModalPersonalTask;
